
import { Component, Prop, Mixins } from "vue-property-decorator"
import { AvailabilityRange, DisplayUserTime, UserTimeForWeekWithUser, ShiftPatternInfo } from "@/types/timeManagement"
import UserImage from "@/components/UserImage.vue"
import { FormatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { UserWithFields } from "@/types/user"
import { IdLabelPair } from "@/types/labeledValue"

@Component({
	components: {
		"user-image": UserImage
	},
	mixins: [formatDateMixin]
})
export default class UserTimeRow extends Mixins(FormatFieldsMixin) {
	@Prop({ required: true })
	public userTime!: DisplayUserTime

	@Prop({ required: true })
	public hours!: number

	@Prop({ required: true })
	public allUserTimes!: UserTimeForWeekWithUser[]

	@Prop({ required: true })
	public excludeFromAvailable!: { [userFieldId: string]: string[] } | null

	@Prop({ required: true })
	public user!: UserWithFields

	@Prop()
	public availabilityCheckRange!: AvailabilityRange

	@Prop({ default: null })
	public assignmentDate!: string | null

	@Prop({ default: null })
	public selectedShiftPattern!: string | null

	@Prop({ default: false })
	public showShiftPatternColumn!: boolean

	// Constants
	private availabilityCheckRangeType = AvailabilityRange
	private TimeManagementDisabledMessage =
		"Time Management is not enabled for this user. You may still assign them if appropriate."

	private ExcludedFromAvailableMessage =
		"This user has been excluded from the available users list. You may still assign them if appropriate."

	private get userHasTimesInPeriod (): boolean {
		return this.allUserTimes.some(userTime => userTime.userId === this.userTime.userId)
	}

	private get userIsExcludedFromAvailable (): boolean {
		if (!this.user?.fieldValues || !this.excludeFromAvailable || Object.keys(this.excludeFromAvailable).length === 0) {
			return false
		}

		for (const [userFieldId, excludeValues] of Object.entries(this.excludeFromAvailable)) {
			const userFieldValue = this.user.fieldValues![userFieldId]?.value
			if (!userFieldValue) continue

			if (typeof userFieldValue === "string") {
				if (excludeValues.includes(userFieldValue)) {
					return true
				}
			} else {
				const listValue = userFieldValue as IdLabelPair[]
				if (listValue.some(value => excludeValues.includes(value.id))) {
					return true
				}
			}
		}

		return false
	}

	private get isBelowHoursRequirement () {
		if (this.availabilityCheckRange !== undefined && this.availabilityCheckRange === AvailabilityRange.OnDate) {
			return this.userTime.assignmentDateRemainingCapacity === undefined || this.userTime.assignmentDateRemainingCapacity < this.hours || !this.userHasTimesInPeriod
		} else {
			return this.userTime.weeklyRemainingCapacity === undefined ||
			this.userTime.weeklyRemainingCapacity < this.hours || !this.userHasTimesInPeriod
		}
	}

	private isShiftPatternMatch (patternTitle: string): boolean {
		if (!this.selectedShiftPattern) {
			return false
		}
		return patternTitle === this.selectedShiftPattern
	}

	private get filteredShiftPatterns (): ShiftPatternInfo[] {
		if (!this.userTime.shiftPatterns || !Array.isArray(this.userTime.shiftPatterns)) {
			return []
		}

		let patterns = this.userTime.shiftPatterns

		// If checking availability on the scheduled date only, show only matching date
		if (this.availabilityCheckRange === AvailabilityRange.OnDate && this.assignmentDate) {
			const assignDate = new Date(this.assignmentDate)
			patterns = patterns.filter(pattern => {
				const shift = new Date(pattern.date)
				return assignDate.getDate() === shift.getDate() &&
					assignDate.getMonth() === shift.getMonth() &&
					assignDate.getFullYear() === shift.getFullYear()
			})
		}

		// Filter by selected shift pattern if one is selected
		if (this.selectedShiftPattern) {
			patterns = patterns.filter(pattern => pattern.title === this.selectedShiftPattern)
		}

		return patterns
	}
}
